import type {
  SpartanCustomizePreviewOption,
  SpartanCustomizePreviewView,
} from '@/app/lcv/components/sections/spartan/products/customize/types';

type PreviewSrcMap = Partial<Record<SpartanCustomizePreviewView, string>>;

export type PreviewSurface = 'preview' | 'thumbnail';

export interface PreviewSurfaceCache {
  readyKeys: ReadonlySet<string>;
  displayedSrcByView: PreviewSrcMap;
}

type PreviewCache = Record<PreviewSurface, PreviewSurfaceCache>;
export type PreviewCacheByModelId = Record<string, PreviewCache>;

export interface PendingPreviewOption {
  modelId: string;
  option: SpartanCustomizePreviewOption;
}

const COLOR_PRELOAD_VIEWS = new Set<SpartanCustomizePreviewView>([
  'front',
  'rear',
]);

export const getPreviewOptionKey = (option: SpartanCustomizePreviewOption) =>
  `${option.view}:${option.src}`;

const isSameOption = (
  a: SpartanCustomizePreviewOption | null,
  b: SpartanCustomizePreviewOption
) => a?.view === b.view && a.src === b.src;

export const isSamePendingOption = (
  pendingOption: PendingPreviewOption | null,
  modelId: string,
  option: SpartanCustomizePreviewOption
) =>
  pendingOption?.modelId === modelId &&
  isSameOption(pendingOption.option, option);

export const getSrcByView = (
  options: SpartanCustomizePreviewOption[]
): PreviewSrcMap =>
  options.reduce<PreviewSrcMap>((acc, option) => {
    acc[option.view] = option.src;
    return acc;
  }, {});

const createPreviewSurfaceCache = (
  options: SpartanCustomizePreviewOption[]
): PreviewSurfaceCache => ({
  readyKeys: new Set<string>(),
  displayedSrcByView: getSrcByView(options),
});

export const createPreviewCache = (
  options: SpartanCustomizePreviewOption[]
): PreviewCache => ({
  preview: createPreviewSurfaceCache(options),
  thumbnail: createPreviewSurfaceCache(options),
});

export const createInitialPreviewCacheByModelId = (
  modelId: string,
  options: SpartanCustomizePreviewOption[]
): PreviewCacheByModelId =>
  modelId
    ? {
        [modelId]: createPreviewCache(options),
      }
    : {};

const ensurePreviewSurfaceCacheViews = (
  cache: PreviewSurfaceCache | undefined,
  options: SpartanCustomizePreviewOption[]
): PreviewSurfaceCache => {
  if (!cache) return createPreviewSurfaceCache(options);

  const selectedSrcByView = getSrcByView(options);
  const hasMissingView = options.some(
    (option) => !cache.displayedSrcByView[option.view]
  );

  if (!hasMissingView) return cache;

  return {
    ...cache,
    displayedSrcByView: {
      ...selectedSrcByView,
      ...cache.displayedSrcByView,
    },
  };
};

export const ensurePreviewCacheViews = (
  cache: PreviewCache | undefined,
  options: SpartanCustomizePreviewOption[]
): PreviewCache => {
  if (!cache) return createPreviewCache(options);

  const preview = ensurePreviewSurfaceCacheViews(cache.preview, options);
  const thumbnail = ensurePreviewSurfaceCacheViews(cache.thumbnail, options);

  if (preview === cache.preview && thumbnail === cache.thumbnail) return cache;

  return {
    preview,
    thumbnail,
  };
};

export const getDisplayedPreviewOptions = (
  options: SpartanCustomizePreviewOption[],
  cache: PreviewSurfaceCache | undefined,
  selectedSrcByView: PreviewSrcMap
) => {
  const displayedSrcByView = cache?.displayedSrcByView ?? selectedSrcByView;

  return options.map((option) => {
    const optionKey = getPreviewOptionKey(option);
    const fallbackSrc = displayedSrcByView[option.view];
    const src =
      cache?.readyKeys.has(optionKey) || !fallbackSrc
        ? option.src
        : fallbackSrc;

    return {
      ...option,
      src,
    };
  });
};

export const getPendingColorOptions = (
  options: SpartanCustomizePreviewOption[],
  cache: PreviewSurfaceCache | undefined
) =>
  options.filter((option) => {
    if (!COLOR_PRELOAD_VIEWS.has(option.view)) return false;

    const optionKey = getPreviewOptionKey(option);
    const fallbackSrc = cache?.displayedSrcByView[option.view];

    return !cache?.readyKeys.has(optionKey) && fallbackSrc !== option.src;
  });

export const createPendingOptions = (
  modelId: string,
  options: SpartanCustomizePreviewOption[]
): PendingPreviewOption[] =>
  options.map((option) => ({
    modelId,
    option,
  }));
